import React, { type ComponentType, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { observer } from 'mobx-react-lite';
import { BrowserRouter as Router, Route, Switch, useHistory, useLocation } from 'react-router-dom';

import AuthLayout from 'app/components/layouts/Auth';
import { type RouteItem } from 'app/models/RouteModels';
import { useStores } from 'app/stores/config/UseStores';

import { authRoutes, dashboardRoutes } from './index';
import DashboardLayout from '../components/layouts/Dashboard';

const SentryRoute = Sentry.withSentryRouting(Route);

type Props = {
  children: React.ReactNode;
};

const CheckAuth = observer(({ children }: Props) => {
  const [busy, setBusy] = useState(false);
  const history = useHistory();
  const location = useLocation();

  const {
    dataStores: { authStore },
  } = useStores();

  const handleLocationChange = async (location: { pathname: string }) => {
    try {
      setBusy(true);

      const isAuthPath = location.pathname.startsWith('/auth');

      if (!authStore.isAuth && !isAuthPath) {
        try {
          const access = await authStore.refresh();
          if (!access) {
            history.replace('/auth');
            return;
          }
          history.replace(location.pathname);
        } catch {
          history.replace('/auth');
          return;
        }
      }

      if (authStore.isAuth) {
        authStore.sync();

        const isRootOrAuth = location.pathname === '/' || isAuthPath;
        if (isRootOrAuth) {
          history.replace('leads');
        }
      }
    } catch (error) {
      console.error('Authentication error:', error);
      history.replace('/auth');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    handleLocationChange(location);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  return busy ? <>Loading...</> : <>{children}</>;
});

const ChildRoutes = ({ layout: Layout, routes }: { layout: ComponentType<object>; routes: RouteItem[] }) => {
  const SuspenseFallback = <>Loading...</>;

  return (
    <Layout>
      <Switch>
        {routes.map((category, index) => {
          const CategoryComponent = category.component as ComponentType;

          return category.children ? (
            // Route item with children
            category.children.map((route, index) => {
              const RouteComponent = route.component as ComponentType;

              return (
                <SentryRoute
                  key={index}
                  path={route.path}
                  exact
                  render={() => {
                    return (
                      <React.Suspense fallback={SuspenseFallback}>
                        <RouteComponent />
                      </React.Suspense>
                    );
                  }}
                />
              );
            })
          ) : (
            // Route item without children
            <SentryRoute
              key={index}
              path={category.path}
              exact
              render={() => {
                return (
                  <React.Suspense fallback={SuspenseFallback}>
                    <CategoryComponent />
                  </React.Suspense>
                );
              }}
            />
          );
        })}
      </Switch>
    </Layout>
  );
};

const Routes = () => {
  return (
    <Router>
      <CheckAuth>
        <Switch>
          {/* Auth routes */}
          <SentryRoute
            path="/auth*"
            exact
            component={() => (
              <ChildRoutes
                layout={AuthLayout}
                routes={authRoutes}
              />
            )}
          />

          {/* Dashboard routes */}
          <SentryRoute
            path="/*"
            exact
            component={() => (
              <ChildRoutes
                layout={DashboardLayout}
                routes={dashboardRoutes}
              />
            )}
          />
        </Switch>
      </CheckAuth>
    </Router>
  );
};

export default Routes;
